import {useEffect, useRef, useState} from "react";
import {faCheck, faInfoCircle, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import axios from "../../api/axios";
import MadeByBRB from "../dashboard/MadeByBRB";

// Neuer E-Mail Regex für die Validierung
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const REGISTER_URL = '/api/v1/register';

const Register = () => {
    const emailRef = useRef();
    const errRef = useRef();

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        emailRef.current.focus();
    }, [])

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email));
    }, [email])

    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [pwd, matchPwd])

    useEffect(() => {
        setErrMsg('');
    }, [email, pwd, matchPwd])

    const handleSubmit = async (e) => {
        e.preventDefault();
        const v1 = EMAIL_REGEX.test(email);
        const v2 = PWD_REGEX.test(pwd);
        if (!v1 || !v2) {
            setErrMsg("Ungültige Eingabe");
            return;
        }
        try {
            let password = pwd;
            const response = await axios.post(REGISTER_URL,
                JSON.stringify({email, password}),
                {
                    headers: {'Content-Type': 'application/json'},
                    withCredentials: true
                }
            );
            console.log(response?.data);
            setSuccess(true);
            setEmail('');
            setPwd('');
            setMatchPwd('');
        } catch (err) {
            if (!err?.response) {
                setErrMsg('Keine Serverantwort');
            } else if (err.response?.status === 409) {
                setErrMsg('E-Mail-Adresse bereits vergeben');
            } else {
                setErrMsg('Registrierung fehlgeschlagen');
            }
            errRef.current.focus();
        }
    }

    return (
        <>
            {success ? (
                <section className="flex justify-center items-center h-screen">
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                        <h1 className="text-2xl font-bold mb-4">Vielen Dank für die Registrierung!</h1>
                        <p>Sie erhalten eine Mail, in der Sie die Registrierung bitte bestätigen.
                            Im Anschluss können Sie sich anmelden.</p>
                        <p>
                            <a href="https://hch.it-brb.app/login" className="text-blue-500 underline">Anmelden</a>
                        </p>
                    </div>
                </section>
            ) : (
                <section className="w-full h-screen flex flex-col justify-between">
                    <div className="flex justify-center items-center h-screen">
                        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                            <p ref={errRef} className={errMsg ? "errmsg text-red-500 mb-4" : "offscreen"}
                               aria-live="assertive">{errMsg}</p>
                            <h1 className="text-2xl font-bold mb-6 text-center">Registrieren</h1>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        E-Mail-Adresse:
                                        <FontAwesomeIcon icon={faCheck}
                                                         className={validEmail ? "text-green-500 ml-2" : "hidden"}/>
                                        <FontAwesomeIcon icon={faTimes}
                                                         className={validEmail || !email ? "hidden" : "text-red-500 ml-2"}/>
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        ref={emailRef}
                                        autoComplete="off"
                                        onChange={(e) => setEmail(e.target.value)}
                                        value={email}
                                        required
                                        aria-invalid={validEmail ? "false" : "true"}
                                        aria-describedby="emailnote"
                                        onFocus={() => setEmailFocus(true)}
                                        onBlur={() => setEmailFocus(false)}
                                        className="mt-1 w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                    <p id="emailnote"
                                       className={emailFocus && email && !validEmail ? "text-xs text-gray-500 mt-1" : "hidden"}>
                                        <FontAwesomeIcon icon={faInfoCircle}/> Bitte geben Sie eine gültige
                                        E-Mail-Adresse ein.
                                    </p>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                        Passwort:
                                        <FontAwesomeIcon icon={faCheck}
                                                         className={validPwd ? "text-green-500 ml-2" : "hidden"}/>
                                        <FontAwesomeIcon icon={faTimes}
                                                         className={validPwd || !pwd ? "hidden" : "text-red-500 ml-2"}/>
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        onChange={(e) => setPwd(e.target.value)}
                                        value={pwd}
                                        required
                                        aria-invalid={validPwd ? "false" : "true"}
                                        aria-describedby="pwdnote"
                                        onFocus={() => setPwdFocus(true)}
                                        onBlur={() => setPwdFocus(false)}
                                        className="mt-1 w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                    <div></div>
                                    <p id="pwdnote"
                                       className={pwdFocus && !validPwd ? "text-sm text-gray-500 mt-1" : "hidden"}>
                                        <FontAwesomeIcon icon={faInfoCircle}/> 8 bis 24 Zeichen. Muss Groß- und
                                        Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten.
                                    </p>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="confirm_pwd" className="block text-sm font-medium text-gray-700">
                                        Passwort bestätigen:
                                        <FontAwesomeIcon icon={faCheck}
                                                         className={validMatch && matchPwd ? "text-green-500 ml-2" : "hidden"}/>
                                        <FontAwesomeIcon icon={faTimes}
                                                         className={validMatch || !matchPwd ? "hidden" : "text-red-500 ml-2"}/>
                                    </label>
                                    <input
                                        type="password"
                                        id="confirm_pwd"
                                        onChange={(e) => setMatchPwd(e.target.value)}
                                        value={matchPwd}
                                        required
                                        aria-invalid={validMatch ? "false" : "true"}
                                        aria-describedby="confirmnote"
                                        onFocus={() => setMatchFocus(true)}
                                        onBlur={() => setMatchFocus(false)}
                                        className="mt-1 w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                    <p id="confirmnote"
                                       className={matchFocus && !validMatch ? "text-xs text-gray-500 mt-1" : "hidden"}>
                                        <FontAwesomeIcon icon={faInfoCircle}/> Die Passwörter müssen übereinstimmen.
                                    </p>
                                </div>

                                <button
                                    disabled={!validEmail || !validPwd || !validMatch}
                                    className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 focus:outline-none disabled:bg-gray-400"
                                >
                                    Registrieren
                                </button>
                            </form>
                            <p className="mt-4 text-center">
                                Bereits registriert? <a href="/login" className="text-blue-500 underline">Anmelden</a>
                            </p>
                        </div>
                    </div>
                    <div className='pb-4'>
                        <MadeByBRB/>
                    </div>

                </section>
            )}
        </>
    );
}

export default Register;
